import React, { useState } from 'react'
import { injectIntl, FormattedMessage, WrappedComponentProps } from 'react-intl'
import { Layout } from 'shared-global-ec-uikit/components/dist/react/esm/components/layout'
import { LinkText } from 'shared-global-ec-uikit/components/dist/react/esm/components/link-text'
import { Search } from 'shared-global-ec-uikit/components/dist/react/esm/components/search'

import { Dropdown } from 'shared-global-ec-uikit/components/dist/react/esm/components/dropdown'
import { Caption } from 'shared-global-ec-uikit/components/dist/react/esm/components/caption'
import { Filters } from '../Filters/typings'
import { getSupportedEUCountry, spacingClassNames } from 'utils'
import { ALL_REGIONS_VALUE } from 'services/Stores/constants'
import TagManager from 'react-gtm-module'
import getConstants from './../../../../constants'
import { BRAND_LIST } from 'typings'
import {
  Body,
  FormRow,
  Label,
} from 'shared-global-ec-uikit/components/dist/react/esm'
import { trackSearchClickEvent } from 'helpers/analytics'

const { IS_EU: isEU, BRAND: brand, COUNTRY: country } = getConstants()
const triggerSearch = (searchTerm: string) => ({
  dataLayer: {
    brand: brand === BRAND_LIST.PL ? brand.toUpperCase() : brand,
    event: 'trackEvent',
    eventCategory: 'storelocation',
    eventAction: 'search',
    eventLabel: searchTerm,
  },
})

type AreasType = { [areaCode: string]: string }

export type Props = {
  searchTerm?: string
  filters: Filters
  areas: AreasType
  areaLabel: string
  allAreasLabel: string
  onSearchByLocation: (limit?: number) => void
  onClearSearchTerm: () => void
  onKeywordChange: (keyword: string) => void
  onAreaChange: (areaCode?: string) => void
  onSearch: (trackSearchCallBack: (resultCount?: number) => void) => void
  onClearLocation: () => void
  countryListEU: {
    label: string
    value: string
  }[]
  totalCount?: number
}

const getOptions = (areas: AreasType = {}, allAreasLabel: string) => {
  const areaOptions = Object.entries(areas).map(([areaCode, areaName]) => ({
    label: areaName,
    value: areaCode,
  }))

  const all = {
    label: allAreasLabel,
    value: ALL_REGIONS_VALUE,
  }

  return [all, ...areaOptions]
}
const SearchField = ({
  intl,
  searchTerm,
  filters,
  areas,
  areaLabel,
  countryListEU,
  allAreasLabel,
  onSearchByLocation,
  onKeywordChange,
  onSearch,
  onAreaChange,
  onClearLocation,
  onClearSearchTerm,
}: Props & WrappedComponentProps) => {
  const [searchValue, setSearchValue] = useState('')

  const trackSearchCallBack = (resultCount?: number) => {
    trackSearchClickEvent(resultCount, searchTerm)
  }
  if (isEU) {
    return (
      <>
        <FormRow error="" fieldCount={1} helptext="">
          <Dropdown
            error={undefined}
            helptext=""
            id="id"
            updateMaxHeight={true}
            label={
              <Label
                color="primary-dark"
                element="p"
                textAlign="left"
                textTransform="normal"
                variant="standard"
              >
                {areaLabel}
              </Label>
            }
            value={
              filters.otherFilters.areaCode
                ? filters.otherFilters.areaCode
                : getSupportedEUCountry(country)
            }
            onChange={(e: any, value: string) => {
              onClearLocation()
              onAreaChange(
                value === getSupportedEUCountry(country) ? undefined : value,
              )
            }}
            options={countryListEU}
          />
        </FormRow>

        <Layout
          colSpan={{
            lg: 9,
            md: 12,
            sm: 4,
          }}
        >
          <div className={spacingClassNames({ sm: 4, md: 4, lg: 4, xl: 4 })}>
            <Search
              clearButtonLabel="Clear"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target
                setSearchValue(value)
                onKeywordChange(value)
              }}
              onClear={() => {
                onClearSearchTerm()
                setSearchValue('')
              }}
              onSearch={() => {
                TagManager.dataLayer(triggerSearch(searchTerm as string))
                onClearLocation()
                onSearch(trackSearchCallBack)
              }}
              placeholder={intl.formatMessage({
                id: 'text_store_locator_search_form_placeholder',
              })}
              searchButtonLabel="Search"
              // Always pass in a value to keep <SearchField /> controlled.
              // Using local state instead of searchTerm prop to fix IME duplication issue
              value={searchValue || ''}
            />
          </div>
          <Caption
            className="fr-ec-mb-spacing-05"
            element="p"
            onClick={() => onSearchByLocation()}
          >
            <LinkText
              ariaLabel="aria Label"
              element="a"
              href="#"
              icon="location"
              iconPosition={undefined}
              variant="standalone-primary"
              wordBreak="break-word"
              ecRenewal
            >
              <Body element="p" textAlign="left" textTransform="normal">
                <FormattedMessage id="text_storesearch_bynearest" />
              </Body>
            </LinkText>
          </Caption>
        </Layout>
      </>
    )
  }
  return (
    <>
      <Layout
        colSpan={{
          lg: 9,
          md: 12,
          sm: 4,
        }}
      >
        <div className={spacingClassNames({ sm: 2, md: 2, lg: 2, xl: 2 })}>
          <Search
            clearButtonLabel="Clear"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              setSearchValue(value)
              onKeywordChange(value)
            }}
            onClear={() => {
              onClearSearchTerm()
              setSearchValue('')
            }}
            onSearch={() => {
              TagManager.dataLayer(triggerSearch(searchTerm as string))
              onClearLocation()
              onSearch(trackSearchCallBack)
            }}
            placeholder={intl.formatMessage({
              id: 'text_store_locator_search_form_placeholder',
            })}
            searchButtonLabel="Search"
            // Always pass in a value to keep <SearchField /> controlled.
            value={searchValue || ''}
          />
        </div>
        <Caption
          className="fr-ec-mb-spacing-06"
          element="p"
          onClick={() => onSearchByLocation()}
        >
          <LinkText
            ariaLabel="aria Label"
            element="a"
            href="#"
            icon="location"
            iconPosition={undefined}
            variant="standalone-primary"
            wordBreak="break-word"
            ecRenewal
          >
            <Body element="p" textAlign="left" textTransform="normal">
              <FormattedMessage id="text_storesearch_bynearest" />
            </Body>
          </LinkText>
        </Caption>
      </Layout>

      <div className="fr-ec-mb-spacing-04">
        <FormRow error="" fieldCount={1} helptext="">
          <Dropdown
            error={undefined}
            helptext=""
            id="id"
            updateMaxHeight={true}
            label={
              <Label
                color="primary-dark"
                element="p"
                textAlign="left"
                textTransform="normal"
                variant="standard"
              >
                {areaLabel}
              </Label>
            }
            value={
              filters.otherFilters.areaCode ? filters.otherFilters.areaCode : ''
            }
            onChange={(e: any, value: string) => {
              onAreaChange(value)
            }}
            options={getOptions(areas, allAreasLabel)}
          />
        </FormRow>
      </div>
    </>
  )
}

export default injectIntl(SearchField)
